import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import FullLayout from 'layout/FullLayout';

// ── Main page ─────────────────────────────────────────────────────────────────
const InfoPricePage = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location.hash]);

  return (
    <FullLayout>
      <div className="bg-[#fcf2e9] min-h-screen pb-24">
        {/* ── Page header ───────────────────────────────────────────────── */}
        <div className="pt-12 pb-8 px-6 sm:px-10 lg:px-16 max-w-7xl mx-auto">
          <div className="flex items-center gap-2 mb-4">
            <span className="h-px w-5 bg-primary-main" />
            <span className="text-[10px] font-black uppercase tracking-[0.4em] text-primary-main">
              KiN Space
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-kin-textdark leading-none">
            Info & <span className="text-primary-main">Price</span>
          </h1>
          <p className="mt-4 text-sm text-kin-text max-w-xl leading-relaxed">
            KiN Space adalah tempat bermain dan edukasi anak dengan konsep exhibition + play —
            menyediakan aktivitas kreatif dan multisensori untuk anak dan keluarga dengan nuansa
            budaya Indonesia.
          </p>
        </div>

        {/* ── Operational Hours ─────────────────────────────────────────── */}
        <section
          id="jam-operasional"
          className="px-6 sm:px-10 lg:px-16 max-w-7xl mx-auto scroll-mt-[80px] py-5">
          <div
            className="relative rounded-3xl overflow-hidden p-4 sm:p-5"
            style={{ backgroundColor: '#F58136' }}>
            {/* White blob accents on orange bg */}
            <div
              className="absolute -bottom-6 -left-6 w-28 h-28 pointer-events-none"
              style={{
                backgroundColor: 'rgba(255,255,255,0.15)',
                borderRadius: '60% 40% 60% 40% / 60% 40% 60% 40%',
              }}
            />
            <div
              className="absolute -top-6 -right-6 w-24 h-24 pointer-events-none"
              style={{
                backgroundColor: 'rgba(255,255,255,0.1)',
                borderRadius: '40% 60% 40% 60% / 40% 60% 40% 60%',
              }}
            />

            {/* Inner cream box */}
            <div
              className="relative rounded-2xl py-10 sm:py-12 px-8 text-center overflow-hidden"
              style={{ backgroundColor: '#f5ede2' }}>
              {/* KiN logo */}
              <div className="flex justify-center mb-4">
                <img
                  src="/kin/assest/logo.png"
                  alt="KiN Space"
                  className="h-7 object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-kin-textdark mb-6">
                Operational Hours
              </h2>
              <div
                className="inline-block px-10 py-4 rounded-xl"
                style={{ backgroundColor: '#F58136' }}>
                <p className="text-3xl sm:text-5xl font-black text-white">9 AM – 9 PM</p>
              </div>
              <p className="text-base text-kin-textdark mt-4">Setiap hari · Senin – Minggu</p>
            </div>
          </div>
        </section>

        {/* ── Weekday + Weekend Passes ──────────────────────────────────── */}
        <section
          id="harga-tiket"
          className="px-6 sm:px-10 lg:px-16 max-w-7xl mx-auto scroll-mt-[80px] pt-5 pb-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* ─ WEEKDAY ─ */}
            <div
              className="relative rounded-3xl overflow-hidden"
              style={{ backgroundColor: '#f5ede2' }}>
              <div className="p-8 sm:p-10">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  {/* Left: title + notes */}
                  <div>
                    <h3 className="text-5xl sm:text-[3.2rem] font-black text-kin-textdark leading-none">
                      WEEKDAY
                      <br />
                      PASSES
                    </h3>
                    <p className="text-base font-bold mt-3 mb-5" style={{ color: '#F58136' }}>
                      2,5 Hour Per Entry!
                    </p>
                    <div className="space-y-2">
                      {[
                        '*Playtime exceeding 2.5 hours will be charged as Full Day.',
                        '*Children over the age of 13 are considered as an adult',
                        '*Under 6 months get in FREE*',
                      ].map((note, i) => (
                        <p key={i} className="text-xs italic leading-snug text-kin-text/65">
                          {note}
                        </p>
                      ))}
                    </div>
                  </div>

                  {/* Right: Price table */}
                  <div className="space-y-6">
                    {[
                      {
                        title: 'EXHIBITION + PLAY',
                        rows: [
                          ['1 Adult + 1 Kid', '205k'],
                          ['Add-On 1 Adult', '80k'],
                        ],
                      },
                      { title: 'EXHIBITION ONLY', rows: [['1 Adult', '80k']] },
                      { title: 'FULL DAY ADD ON', rows: [['1 Adult + Kid', '100k']] },
                    ].map((cat, i) => (
                      <div key={i}>
                        <p className="text-[11px] font-black text-kin-textdark uppercase tracking-wide mb-2">
                          {cat.title}
                        </p>
                        {cat.rows.map(([label, price], j) => (
                          <div key={j} className="flex justify-between items-baseline py-0.5">
                            <p className="text-sm text-kin-text">{label}</p>
                            <p className="text-sm font-bold text-kin-textdark">{price}</p>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Blob area */}
              <div className="relative h-28 overflow-hidden pointer-events-none">
                <div
                  className="absolute"
                  style={{
                    bottom: '-20px',
                    left: '-15px',
                    width: '140px',
                    height: '155px',
                    backgroundColor: '#F58136',
                    borderRadius: '70% 30% 30% 70% / 70% 70% 30% 30%',
                    opacity: 0.85,
                  }}
                />
                <div
                  className="absolute"
                  style={{
                    bottom: '-15px',
                    left: '95px',
                    width: '120px',
                    height: '145px',
                    backgroundColor: '#F5C430',
                    borderRadius: '40% 60% 60% 40% / 40% 40% 60% 60%',
                    opacity: 0.85,
                  }}
                />
                <div
                  className="absolute"
                  style={{
                    bottom: '-20px',
                    right: '-10px',
                    width: '140px',
                    height: '155px',
                    backgroundColor: '#73C8AF',
                    borderRadius: '60% 40% 40% 60% / 60% 60% 40% 40%',
                    opacity: 0.85,
                  }}
                />
                <div
                  className="absolute"
                  style={{
                    bottom: '-15px',
                    right: '105px',
                    width: '110px',
                    height: '140px',
                    backgroundColor: '#F58136',
                    borderRadius: '40% 60% 70% 30% / 50% 60% 40% 50%',
                    opacity: 0.8,
                  }}
                />
              </div>
            </div>

            {/* ─ WEEKEND ─ */}
            <div
              className="relative rounded-3xl overflow-hidden"
              style={{ backgroundColor: '#f5ede2' }}>
              <div className="p-8 sm:p-10">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  {/* Left: title + notes */}
                  <div>
                    <h3 className="text-5xl sm:text-[3.2rem] font-black text-kin-textdark leading-none">
                      WEEKEND
                      <br />
                      PASSES
                    </h3>
                    <p className="text-base font-bold mt-3 mb-5" style={{ color: '#F58136' }}>
                      2,5 Hour Per Entry!
                    </p>
                    <div className="space-y-2">
                      {[
                        '*Playtime exceeding 2.5 hours will be charged as Full Day.',
                        '*Children over the age of 13 are considered as an adult',
                        '*Under 6 months get in FREE*',
                      ].map((note, i) => (
                        <p key={i} className="text-xs italic leading-snug text-kin-text/65">
                          {note}
                        </p>
                      ))}
                    </div>
                  </div>

                  {/* Right: Price table */}
                  <div className="space-y-6">
                    {[
                      {
                        title: 'EXHIBITION + PLAY',
                        rows: [
                          ['1 Adult + 1 Kid', '250k'],
                          ['Add-On 1 Adult', '115k'],
                        ],
                      },
                      { title: 'EXHIBITION ONLY', rows: [['1 Adult', '95k']] },
                      { title: 'FULL DAY ADD ON', rows: [['1 Adult + Kid', '100k']] },
                    ].map((cat, i) => (
                      <div key={i}>
                        <p className="text-[11px] font-black text-kin-textdark uppercase tracking-wide mb-2">
                          {cat.title}
                        </p>
                        {cat.rows.map(([label, price], j) => (
                          <div key={j} className="flex justify-between items-baseline py-0.5">
                            <p className="text-sm text-kin-text">{label}</p>
                            <p className="text-sm font-bold text-kin-textdark">{price}</p>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Blob area */}
              <div className="relative h-28 overflow-hidden pointer-events-none">
                <div
                  className="absolute"
                  style={{
                    bottom: '-20px',
                    left: '-15px',
                    width: '140px',
                    height: '155px',
                    backgroundColor: '#F5C430',
                    borderRadius: '70% 30% 30% 70% / 70% 70% 30% 30%',
                    opacity: 0.85,
                  }}
                />
                <div
                  className="absolute"
                  style={{
                    bottom: '-15px',
                    left: '95px',
                    width: '120px',
                    height: '145px',
                    backgroundColor: '#F58136',
                    borderRadius: '40% 60% 60% 40% / 40% 40% 60% 60%',
                    opacity: 0.8,
                  }}
                />
                <div
                  className="absolute"
                  style={{
                    bottom: '-20px',
                    right: '-10px',
                    width: '140px',
                    height: '155px',
                    backgroundColor: '#73C8AF',
                    borderRadius: '60% 40% 40% 60% / 60% 60% 40% 40%',
                    opacity: 0.85,
                  }}
                />
                <div
                  className="absolute"
                  style={{
                    bottom: '-15px',
                    right: '105px',
                    width: '110px',
                    height: '140px',
                    backgroundColor: '#F58136',
                    borderRadius: '40% 60% 70% 30% / 50% 60% 40% 50%',
                    opacity: 0.75,
                  }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* ── KiN's Day Thursday ────────────────────────────────────────── */}
        <section
          id="kin-thursday"
          className="px-6 sm:px-10 lg:px-16 max-w-7xl mx-auto scroll-mt-[80px] pb-5">
          <div
            className="relative rounded-3xl overflow-hidden"
            style={{ backgroundColor: '#f5ede2' }}>
            {/* Content */}
            <div className="p-8 sm:p-10 lg:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-[280px,1fr] gap-8 lg:gap-16">
                {/* LEFT: Big stacked title */}
                <div>
                  <h2
                    className="font-black leading-none"
                    style={{ color: '#F58136', fontSize: 'clamp(2.8rem,5vw,3.8rem)' }}>
                    KiN's
                  </h2>
                  <h2
                    className="font-black leading-none"
                    style={{ color: '#73C8AF', fontSize: 'clamp(4rem,7vw,5.5rem)' }}>
                    DAY
                  </h2>
                  <h2
                    className="font-black leading-none mb-7"
                    style={{ color: '#F58136', fontSize: 'clamp(2.8rem,5vw,3.8rem)' }}>
                    Thursday
                  </h2>
                  <div
                    className="inline-block px-4 py-2.5 rounded-xl"
                    style={{ backgroundColor: '#73C8AF' }}>
                    <p className="text-sm font-black text-white leading-tight">
                      Free Exhibition Entry
                    </p>
                    <p className="text-sm font-black text-white leading-tight">Every Thursday</p>
                  </div>
                </div>

                {/* RIGHT: Description */}
                <div className="flex flex-col justify-center">
                  <p className="text-sm text-kin-text leading-relaxed mb-4">
                    KiN's Day Thursday is a free weekly KiNstallation programme, held every Thursday
                    from 2–8 PM, designed to ensure equal access to arts and cultural education for
                    children and families. By removing financial barriers, this programme enables
                    children from diverse community groups and foundations to experience creative
                    learning in a safe, welcoming space.
                  </p>
                  <p className="text-sm text-kin-text leading-relaxed">
                    Through play-based and multisensory activities, KiN's Day Thursday introduces
                    Indonesian culture while nurturing curiosity, creativity, and confidence in
                    young learners. With the support of CSR partners, this initiative brings
                    meaningful cultural experiences closer to families, schools, foundations, and
                    young learners who may otherwise have limited access to arts education in
                    Jakarta.
                  </p>
                </div>
              </div>

              {/* Bottom strip: badges + time + support */}
              <div className="flex flex-wrap items-center gap-4 mt-8 pt-6 border-t border-black/[0.06]">
                {' '}
                <div
                  className="flex-shrink-0 px-8 py-3 rounded-lg"
                  style={{ backgroundColor: '#F58136' }}>
                  <p className="text-2xl font-black text-white">2pm – 8pm</p>
                </div>
                <div className="flex-1 flex justify-end">
                  <div className="text-right">
                    <p className="text-[9px] font-bold text-kin-text/60 uppercase tracking-wider mb-1">
                      Supported by
                    </p>
                    <p className="text-sm font-black text-kin-textdark">Putra Peduli Foundation</p>
                    <p className="text-[10px] text-kin-text/60 italic">Loving · Caring · Sharing</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Blob decoration area */}
            <div className="relative h-28 overflow-hidden pointer-events-none">
              <div
                className="absolute"
                style={{
                  bottom: '-20px',
                  left: '-15px',
                  width: '140px',
                  height: '155px',
                  backgroundColor: '#F5C430',
                  borderRadius: '60% 40% 70% 30% / 60% 50% 50% 60%',
                  opacity: 0.9,
                }}
              />
              <div
                className="absolute"
                style={{
                  bottom: '-15px',
                  left: '90px',
                  width: '120px',
                  height: '145px',
                  backgroundColor: '#F58136',
                  borderRadius: '40% 60% 40% 70% / 50% 60% 40% 50%',
                  opacity: 0.85,
                }}
              />
              <div
                className="absolute"
                style={{
                  bottom: '-20px',
                  right: '-10px',
                  width: '150px',
                  height: '155px',
                  backgroundColor: '#73C8AF',
                  borderRadius: '70% 30% 50% 50% / 40% 60% 40% 60%',
                  opacity: 0.85,
                }}
              />
              <div
                className="absolute"
                style={{
                  bottom: '-15px',
                  right: '110px',
                  width: '115px',
                  height: '140px',
                  backgroundColor: '#F58136',
                  borderRadius: '40% 60% 60% 40% / 60% 40% 60% 40%',
                  opacity: 0.8,
                }}
              />
            </div>
          </div>
        </section>
      </div>
    </FullLayout>
  );
};

export default InfoPricePage;
